/**
 * Background Live Update check, run *after* the app shell is already on screen.
 *
 * This used to be a splash-held gate that first paint awaited: check → download
 * → activate → reload, up to 8s with the router outlet unmounted behind it. On a
 * cold start opened from a notification the user watched the flat `#202020`
 * window for that entire time, because nothing renders until the gate resolves.
 *
 * Nothing here blocks rendering now. A downloaded bundle is *armed* with
 * `setNextBundle` and picked up by the next cold start instead of being reloaded
 * underneath someone who is already using the app. That also deletes the
 * mid-session reload, so there is no window where stale UI shows before a reload
 * and no way to spin a reload loop.
 *
 * LiveUpdate.ready() remains the Capawesome reload-loop / rollback handshake.
 */

/**
 * Upper bound for the whole background check, including the download. It no
 * longer delays first paint, so it only exists to stop a stalled download from
 * hanging around indefinitely.
 */
export const LIVE_UPDATE_BACKGROUND_TIMEOUT_MS = 60_000;

/**
 * Absolute SplashScreen.hide failsafe. First paint no longer waits on the update
 * check, so this now only guards against the app shell failing to mount at all.
 */
export const SPLASH_FAILSAFE_MS = 10_000;

export type LiveUpdateStartupAction = 'none' | 'force_update' | 'update_armed' | 'continue';

export type LiveUpdateStartupResult = {
  action: LiveUpdateStartupAction;
};

export type LiveUpdateTarget = {
  version: string;
};

export type LiveUpdateStatus = {
  forceUpdate: LiveUpdateTarget | null;
  liveUpdate: LiveUpdateTarget | null;
};

export type LiveUpdateStartupDeps = {
  ready: () => Promise<void>;
  /** Single read backing both decisions — these were two identical Firestore queries. */
  getUpdateStatus: () => Promise<LiveUpdateStatus>;
  getBundles: () => Promise<{ bundleIds: string[] }>;
  downloadBundle: (opts: { url: string; bundleId: string }) => Promise<void>;
  setNextBundle: (opts: { bundleId: string }) => Promise<void>;
  buildDownloadUrl: (version: string) => string;
  timeoutMs?: number;
  /** Optional: observe deferred background downloads in tests */
  onBackgroundDownload?: (version: string) => void;
};

export const withTimeout = async <T>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
  if (ms <= 0) {
    throw new Error(`${label} timed out after ${ms}ms`);
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
};

/**
 * Point the *next* launch at the new bundle. Deliberately no `reload()`: the
 * shell is already interactive by the time this runs, and tearing the WebView
 * out from under the user is exactly the jank this path is meant to avoid.
 */
const armNextLaunch = async (deps: LiveUpdateStartupDeps, version: string): Promise<LiveUpdateStartupResult> => {
  await deps.setNextBundle({ bundleId: version });
  return { action: 'update_armed' };
};

/** Best-effort prefetch after a genuine fallback — never blocks the current launch. */
const downloadForNextLaunch = (deps: LiveUpdateStartupDeps, version: string) => {
  deps.onBackgroundDownload?.(version);
  void (async () => {
    try {
      const bundles = await deps.getBundles();
      if (bundles.bundleIds.includes(version)) return;
      await deps.downloadBundle({
        url: deps.buildDownloadUrl(version),
        bundleId: version,
      });
    } catch {
      // Ignore — next cold start will try again if still needed.
    }
  })();
};

const runStartupWork = async (
  deps: LiveUpdateStartupDeps,
  trackPrefetchCandidate: (version: string) => void
): Promise<LiveUpdateStartupResult> => {
  // Capawesome rollback handshake — must run before we consider the new bundle healthy.
  try {
    await deps.ready();
  } catch {
    // Continue with the currently installed bundle.
  }

  let status: LiveUpdateStatus;
  try {
    status = await deps.getUpdateStatus();
  } catch {
    return { action: 'continue' };
  }

  if (status.forceUpdate) {
    return { action: 'force_update' };
  }

  const target = status.liveUpdate;
  if (!target?.version) {
    return { action: 'none' };
  }

  trackPrefetchCandidate(target.version);

  let alreadyReady = false;
  try {
    const bundles = await deps.getBundles();
    alreadyReady = bundles.bundleIds.includes(target.version);
  } catch {
    alreadyReady = false;
  }

  if (alreadyReady) {
    try {
      return await armNextLaunch(deps, target.version);
    } catch {
      return { action: 'continue' };
    }
  }

  try {
    await deps.downloadBundle({
      url: deps.buildDownloadUrl(target.version),
      bundleId: target.version,
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : '';
    if (!message.includes('exists')) {
      downloadForNextLaunch(deps, target.version);
      return { action: 'continue' };
    }
  }

  try {
    return await armNextLaunch(deps, target.version);
  } catch {
    return { action: 'continue' };
  }
};

/**
 * Run the background Live Update check under a single max timeout.
 * Never throws — the caller has already revealed the app and only uses the
 * result to decide whether to raise the force-update wall.
 */
export const runLiveUpdateStartup = async (deps: LiveUpdateStartupDeps): Promise<LiveUpdateStartupResult> => {
  const timeoutMs = deps.timeoutMs ?? LIVE_UPDATE_BACKGROUND_TIMEOUT_MS;
  let prefetchCandidate: string | null = null;

  try {
    return await withTimeout(
      runStartupWork(deps, version => {
        prefetchCandidate = version;
      }),
      timeoutMs,
      'backgroundLiveUpdate'
    );
  } catch {
    // Deadline hit (or unexpected race rejection) — stay on the current bundle.
    if (prefetchCandidate) {
      downloadForNextLaunch(deps, prefetchCandidate);
    }
    return { action: 'continue' };
  }
};
